import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiArrowLeft,
    FiCheckCircle,
    FiChevronDown,
    FiChevronUp,
    FiCreditCard,
    FiFileText,
    FiSettings,
    FiLoader,
    FiMessageSquare,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedSaleInvoice,
    confirmSaleInvoice,
    createSaleInvoice,
    fetchSaleInvoiceById,
    fetchSaleInvoiceCreateData,
    saleInvoiceCreateDataSelector,
    saleInvoiceLoadingSelector,
    selectedSaleInvoiceSelector,
    updateSaleInvoice,
} from "../../../../../../store/billing";

import CustomerSection, {
    EMPTY_CUSTOMER,
    normalizeCustomerValue,
    validateCustomer,
} from "./components/CustomerSection";

import InvoiceHeader, {
    EMPTY_INVOICE_HEADER,
    normalizeHeaderValue,
    toDateInputValue,
} from "./components/InvoiceHeader";

import InvoiceItemsTable, {
    calculateInvoiceItem,
    createEmptyInvoiceItem,
    normalizeInvoiceItem,
    validateInvoiceItem,
} from "./components/InvoiceItemsTable";

import InvoiceTotals, {
    EMPTY_INVOICE_TOTALS,
    calculateInvoiceTotals,
    validateInvoiceTotals,
} from "./components/InvoiceTotals";

import InvoiceActions, {
    ACTION_KEYS,
} from "./components/InvoiceActions";

import "./SaleInvoiceFormPage.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAYMENT_METHODS = [
    {
        value: "CASH",
        label: "Cash",
    },
    {
        value: "UPI",
        label: "UPI",
    },
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
    },
    {
        value: "CARD",
        label: "Card",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
    },
    {
        value: "WALLET",
        label: "Wallet",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];

const EMPTY_PAYMENT = {
    method: "CASH",
    referenceNumber: "",
    transactionId: "",
    paymentDate:
        toDateInputValue(
            new Date()
        ),
    notes: "",
};

const EMPTY_NOTES = {
    customerNotes: "",
    termsAndConditions: "",
    internalNotes: "",
    footerText: "",
};

const LIST_ROUTE =
    "/billing/sale-invoices";

const CREATE_ROUTE =
    "/billing/sale-invoices/create";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const normalizeText = (
    value
) => String(
    value ?? ""
).trim();

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const getEntityId = (
    value
) => {
    if (
        value &&
        typeof value ===
            "object"
    ) {
        return (
            value._id ||
            value.id ||
            ""
        );
    }

    return value || "";
};

const getResultDocument = (
    result
) => {
    const payload =
        result?.payload ||
        result ||
        {};

    return (
        payload?.invoice ||
        payload?.saleInvoice ||
        payload?.document ||
        payload?.data?.invoice ||
        payload?.data
            ?.saleInvoice ||
        payload?.data
            ?.document ||
        (
            payload?._id
                ? payload
                : null
        )
    );
};

const getErrorMessage = (
    error,
    fallback =
        "Unable to complete the operation."
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.message ||
        error?.payload ||
        error?.response
            ?.data?.message ||
        fallback
    );
};

const getCreateDataArray = (
    createData,
    ...keys
) => {
    for (
        const key of
        keys
    ) {
        if (
            Array.isArray(
                createData?.[key]
            )
        ) {
            return createData[
                key
            ];
        }

        if (
            Array.isArray(
                createData
                    ?.dropdowns?.[
                    key
                ]
            )
        ) {
            return createData
                .dropdowns[
                key
            ];
        }
    }

    return [];
};

const createInitialFormState = (
    createData = {}
) => {
    const settings =
        createData.settings ||
        createData
            .billingSettings ||
        {};

    const defaultValues =
        createData.defaultValues ||
        {};

    const documentDate =
        toDateInputValue(
            defaultValues
                .documentDate ||
            new Date()
        );

    const paymentTermDays =
        toNumber(
            settings
                .defaultPaymentTermsDays ??
            defaultValues
                .paymentTermDays ??
            0
        );

    const dueDate =
        toDateInputValue(
            defaultValues
                .dueDate
        );

    const defaults = {
        defaultUnit:
            settings.defaultUnit ||
            defaultValues.unit ||
            "piece",

        defaultPriceMode:
            settings.defaultPriceMode ||
            defaultValues.priceMode ||
            "TAX_EXCLUSIVE",

        /*
        |--------------------------------------------------------------------------
        | DEFAULT ITEM TAX
        |--------------------------------------------------------------------------
        |
        | A new/manual invoice row should start with no tax selected.
        | Product selection can still apply the product's own GST rate.
        |
        |--------------------------------------------------------------------------
        */

        defaultTaxPercentage:
            0,
    };

    return {
        customer: {
            ...EMPTY_CUSTOMER,
        },

        header: {
            ...EMPTY_INVOICE_HEADER,

            documentNumber:
                createData
                    .invoiceNumberPreview ||
                "",

            documentDate,

            dueDate,

            paymentTermDays,

            salesChannel:
                defaultValues
                    .salesChannel ||
                "STORE",

            sourceType:
                defaultValues
                    .sourceType ||
                "MANUAL",

            firmId:
                getEntityId(
                    settings
                        .defaultFirm ||
                    settings
                        .defaultFirmId
                ),

            branchId:
                getEntityId(
                    settings
                        .defaultBranch ||
                    settings
                        .defaultBranchId
                ),

            warehouseId:
                getEntityId(
                    settings
                        .defaultWarehouse ||
                    settings
                        .defaultWarehouseId
                ),

            placeOfSupply:
                settings.state ||
                "",

            placeOfSupplyStateCode:
                settings.stateCode ||
                "",

            isInterState:
                false,
        },

        items: [
            createEmptyInvoiceItem(
                defaults
            ),
        ],

        totals: {
            ...EMPTY_INVOICE_TOTALS,
        },

        payment: {
            ...EMPTY_PAYMENT,
            paymentDate:
                documentDate,
        },

        notes: {
            ...EMPTY_NOTES,

            customerNotes:
                defaultValues
                    .customerNotes ||
                settings
                    .defaultCustomerNotes ||
                "",

            termsAndConditions:
                defaultValues
                    .termsAndConditions ||
                settings
                    .defaultTerms ||
                "",

            footerText:
                defaultValues
                    .footerText ||
                settings
                    .defaultFooterText ||
                "",
        },

        defaults,
    };
};

const mapInvoiceToFormState = (
    invoice,
    createData = {}
) => {
    const initial =
        createInitialFormState(
            createData
        );

    const customerSource = {
        customerId:
            getEntityId(
                invoice
                    ?.customer
            ),

        customerMode:
            invoice?.customer
                ? "SAVED"
                : "MANUAL",

        ...(
            invoice
                ?.customerSnapshot ||
            {}
        ),

        billingAddress:
            invoice
                ?.billingAddressSnapshot ||
            {},

        shippingAddress:
            invoice
                ?.shippingAddressSnapshot ||
            {},

        sameAsBillingAddress:
            invoice
                ?.sameAsBillingAddress !==
            false,
    };

    const headerSource = {
        documentNumber:
            invoice
                ?.documentNumber ||
            "",

        documentDate:
            invoice
                ?.documentDate,

        dueDate:
            invoice?.dueDate,

        referenceNumber:
            invoice
                ?.referenceNumber ||
            "",

        purchaseOrderNumber:
            invoice
                ?.purchaseOrderNumber ||
            "",

        salespersonId:
            getEntityId(
                invoice
                    ?.salesperson
            ),

        salesperson:
            invoice
                ?.salesperson
                ?.name ||
            invoice
                ?.salespersonName ||
            "",

        salesChannel:
            invoice
                ?.salesChannel ||
            "STORE",

        sourceType:
            invoice
                ?.sourceType ||
            "MANUAL",

        firmId:
            getEntityId(
                invoice?.firm
            ),

        branchId:
            getEntityId(
                invoice?.branch
            ),

        warehouseId:
            getEntityId(
                invoice
                    ?.warehouse
            ),

        placeOfSupply:
            invoice
                ?.placeOfSupply ||
            "",

        placeOfSupplyStateCode:
            invoice
                ?.placeOfSupplyStateCode ||
            "",

        isInterState:
            invoice
                ?.isInterState ===
            true,

        paymentTermDays:
            invoice
                ?.paymentTermDays ||
            0,

        shippingMethod:
            invoice
                ?.shippingMethod ||
            invoice
                ?.dispatchDetails
                ?.shippingMethod ||
            "",

        transporterName:
            invoice
                ?.transporterName ||
            invoice
                ?.dispatchDetails
                ?.transporterName ||
            "",

        vehicleNumber:
            invoice
                ?.vehicleNumber ||
            invoice
                ?.dispatchDetails
                ?.vehicleNumber ||
            "",

        documentStatus:
            invoice
                ?.documentStatus ||
            "DRAFT",
    };

    const mappedItems =
        Array.isArray(
            invoice?.items
        ) &&
        invoice.items.length
            ? invoice.items.map(
                (
                    item
                ) =>
                    normalizeInvoiceItem(
                        {
                            ...item,

                            productId:
                                getEntityId(
                                    item
                                        ?.product
                                ),

                            availableStock:
                                item
                                    ?.metadata
                                    ?.availableStock ??
                                item
                                    ?.availableStock ??
                                0,
                        },
                        initial.defaults
                    )
            )
            : initial.items;

    const savedTotals =
        invoice?.totals ||
        {};

    return {
        ...initial,

        customer:
            normalizeCustomerValue(
                customerSource
            ),

        header:
            normalizeHeaderValue(
                headerSource
            ),

        items:
            mappedItems,

        totals: {
            ...initial.totals,
            ...savedTotals,

            invoiceDiscountType:
                savedTotals
                    .invoiceDiscountType ||
                invoice
                    ?.invoiceDiscountType ||
                "NONE",

            invoiceDiscountValue:
                savedTotals
                    .invoiceDiscountValue ??
                invoice
                    ?.invoiceDiscountValue ??
                0,

            shippingCharge:
                savedTotals
                    .shippingCharge ??
                invoice
                    ?.shippingCharge ??
                0,

            packingCharge:
                savedTotals
                    .packingCharge ??
                invoice
                    ?.packingCharge ??
                0,

            otherCharge:
                savedTotals
                    .otherCharge ??
                invoice
                    ?.otherCharge ??
                0,

            roundOffEnabled:
                savedTotals
                    .roundOffEnabled !==
                false,

            paidAmount:
                savedTotals
                    .paidAmount ??
                invoice
                    ?.paymentSummary
                    ?.paidAmount ??
                0,
        },

        notes: {
            customerNotes:
                invoice
                    ?.customerNotes ||
                "",

            termsAndConditions:
                invoice
                    ?.termsAndConditions ||
                "",

            internalNotes:
                invoice
                    ?.internalNotes ||
                "",

            footerText:
                invoice
                    ?.footerText ||
                "",
        },
    };
};

const calculateItemTotals = (
    items,
    isInterState
) => {
    return items.reduce(
        (
            accumulator,
            item
        ) => {
            const calculated =
                calculateInvoiceItem(
                    item,
                    isInterState
                );

            return {
                grossAmount:
                    roundMoney(
                        accumulator
                            .grossAmount +
                        calculated
                            .grossAmount
                    ),

                discountAmount:
                    roundMoney(
                        accumulator
                            .discountAmount +
                        calculated
                            .discountAmount
                    ),

                taxableAmount:
                    roundMoney(
                        accumulator
                            .taxableAmount +
                        calculated
                            .taxableAmount
                    ),

                cgstAmount:
                    roundMoney(
                        accumulator
                            .cgstAmount +
                        calculated
                            .cgstAmount
                    ),

                sgstAmount:
                    roundMoney(
                        accumulator
                            .sgstAmount +
                        calculated
                            .sgstAmount
                    ),

                igstAmount:
                    roundMoney(
                        accumulator
                            .igstAmount +
                        calculated
                            .igstAmount
                    ),

                cessAmount:
                    roundMoney(
                        accumulator
                            .cessAmount +
                        calculated
                            .cessAmount
                    ),

                totalAmount:
                    roundMoney(
                        accumulator
                            .totalAmount +
                        calculated
                            .totalAmount
                    ),
            };
        },
        {
            grossAmount: 0,
            discountAmount: 0,
            taxableAmount: 0,
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0,
            cessAmount: 0,
            totalAmount: 0,
        }
    );
};

const buildInvoicePayload = ({
    customer,
    header,
    items,
    totals,
    notes,
    includePayment =
        false,
    payment,
}) => {
    const normalizedItems =
        items.map(
            (
                item
            ) => ({
                productId:
                    item.productId ||
                    null,

                itemType:
                    item.itemType ||
                    (
                        item
                            .productId
                            ? "INVENTORY"
                            : "MANUAL"
                    ),

                itemName:
                    normalizeText(
                        item.itemName
                    ),

                description:
                    normalizeText(
                        item.description
                    ),

                sku:
                    normalizeText(
                        item.sku
                    ),

                barcode:
                    normalizeText(
                        item.barcode
                    ),

                brand:
                    normalizeText(
                        item.brand
                    ),

                modelNumber:
                    normalizeText(
                        item.modelNumber
                    ),

                hsnSacCode:
                    normalizeText(
                        item.hsnSacCode
                    ).toUpperCase(),

                quantity:
                    toNumber(
                        item.quantity
                    ),

                unit:
                    normalizeText(
                        item.unit
                    ) ||
                    "piece",

                rate:
                    roundMoney(
                        item.rate
                    ),

                priceMode:
                    item.priceMode ||
                    "TAX_EXCLUSIVE",

                discountType:
                    item.discountType ||
                    "NONE",

                discountValue:
                    roundMoney(
                        item.discountValue
                    ),

                taxPercentage:
                    toNumber(
                        item
                            .taxPercentage
                    ),

                cessPercentage:
                    toNumber(
                        item
                            .cessPercentage
                    ),

                warranty:
                    normalizeText(
                        item.warranty
                    ),

                warrantyMonths:
                    toNumber(
                        item
                            .warrantyMonths
                    ),

                metadata: {
                    ...(item.metadata ||
                        {}),
                },
            })
        );

    const customerSnapshot = {
        customerName:
            normalizeText(
                customer
                    .customerName
            ),

        mobile:
            normalizeText(
                customer.mobile
            ),

        alternateMobile:
            normalizeText(
                customer
                    .alternateMobile
            ),

        email:
            normalizeText(
                customer.email
            ).toLowerCase(),

        companyName:
            normalizeText(
                customer
                    .companyName
            ),

        customerType:
            customer
                .customerType ||
            "CONSUMER",

        taxTreatment:
            customer
                .taxTreatment ||
            "CONSUMER",

        gstin:
            normalizeText(
                customer.gstin
            ).toUpperCase(),

        pan:
            normalizeText(
                customer.pan
            ).toUpperCase(),
    };

    const billingAddressSnapshot = {
        name:
            customerSnapshot
                .customerName,

        companyName:
            customerSnapshot
                .companyName,

        mobile:
            customerSnapshot
                .mobile,

        email:
            customerSnapshot
                .email,

        gstin:
            customerSnapshot
                .gstin,

        ...(
            customer
                .billingAddress ||
            {}
        ),
    };

    const shippingAddressSnapshot =
        customer
            .sameAsBillingAddress
            ? {
                ...billingAddressSnapshot,
            }
            : {
                name:
                    customerSnapshot
                        .customerName,

                companyName:
                    customerSnapshot
                        .companyName,

                mobile:
                    customerSnapshot
                        .mobile,

                email:
                    customerSnapshot
                        .email,

                ...(
                    customer
                        .shippingAddress ||
                    {}
                ),
            };

    const payload = {
        customerId:
            customer.customerId ||
            null,

        customerSnapshot,

        billingAddressSnapshot,

        shippingAddressSnapshot,

        sameAsBillingAddress:
            customer
                .sameAsBillingAddress !==
            false,

        documentDate:
            header
                .documentDate,

        dueDate:
            header.dueDate ||
            null,

        referenceNumber:
            normalizeText(
                header
                    .referenceNumber
            ),

        purchaseOrderNumber:
            normalizeText(
                header
                    .purchaseOrderNumber
            ),

        salespersonId:
            header
                .salespersonId ||
            null,

        salesChannel:
            header
                .salesChannel ||
            "STORE",

        sourceType:
            header.sourceType ||
            "MANUAL",

        firmId:
            header.firmId ||
            null,

        branchId:
            header.branchId ||
            null,

        warehouseId:
            header
                .warehouseId ||
            null,

        placeOfSupply:
            normalizeText(
                header
                    .placeOfSupply
            ),

        placeOfSupplyStateCode:
            normalizeText(
                header
                    .placeOfSupplyStateCode
            ),

        isInterState:
            Boolean(
                header
                    .isInterState
            ),

        paymentTermDays:
            toNumber(
                header
                    .paymentTermDays
            ),

        shippingMethod:
            normalizeText(
                header
                    .shippingMethod
            ),

        transporterName:
            normalizeText(
                header
                    .transporterName
            ),

        vehicleNumber:
            normalizeText(
                header
                    .vehicleNumber
            ).toUpperCase(),

        items:
            normalizedItems,

        invoiceDiscountType:
            totals
                .invoiceDiscountType ||
            "NONE",

        invoiceDiscountValue:
            roundMoney(
                totals
                    .invoiceDiscountValue
            ),

        shippingCharge:
            roundMoney(
                totals
                    .shippingCharge
            ),

        packingCharge:
            roundMoney(
                totals
                    .packingCharge
            ),

        otherCharge:
            roundMoney(
                totals
                    .otherCharge
            ),

        roundOffEnabled:
            totals
                .roundOffEnabled !==
            false,

        customerNotes:
            normalizeText(
                notes
                    .customerNotes
            ),

        termsAndConditions:
            normalizeText(
                notes
                    .termsAndConditions
            ),

        internalNotes:
            normalizeText(
                notes
                    .internalNotes
            ),

        footerText:
            normalizeText(
                notes.footerText
            ),

        documentStatus:
            "DRAFT",
    };

    if (
        includePayment &&
        roundMoney(
            totals.paidAmount
        ) >
            0
    ) {
        payload.payments = [
            {
                amount:
                    roundMoney(
                        totals
                            .paidAmount
                    ),

                paymentMethod:
                    payment.method ||
                    "CASH",

                method:
                    payment.method ||
                    "CASH",

                paymentDate:
                    payment
                        .paymentDate ||
                    header
                        .documentDate,

                referenceNumber:
                    normalizeText(
                        payment
                            .referenceNumber
                    ),

                transactionId:
                    normalizeText(
                        payment
                            .transactionId
                    ),

                notes:
                    normalizeText(
                        payment.notes
                    ),
            },
        ];
    }

    return payload;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoiceFormPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const { id } =
        useParams();

    const isEditMode =
        Boolean(id);

    const createData =
        useSelector(
            saleInvoiceCreateDataSelector
        ) || {};

    const selectedInvoice =
        useSelector(
            selectedSaleInvoiceSelector
        );

    const loadingState =
        useSelector(
            saleInvoiceLoadingSelector
        ) || {};

    const [
        customer,
        setCustomer,
    ] = useState({
        ...EMPTY_CUSTOMER,
    });

    const [
        header,
        setHeader,
    ] = useState({
        ...EMPTY_INVOICE_HEADER,

        documentDate:
            toDateInputValue(
                new Date()
            ),
    });

    const [
        items,
        setItems,
    ] = useState([
        createEmptyInvoiceItem(),
    ]);

    const [
        totals,
        setTotals,
    ] = useState({
        ...EMPTY_INVOICE_TOTALS,
    });

    const [
        payment,
        setPayment,
    ] = useState({
        ...EMPTY_PAYMENT,
    });

    const [
        notes,
        setNotes,
    ] = useState({
        ...EMPTY_NOTES,
    });

    const [
        notesOpen,
        setNotesOpen,
    ] = useState(false);

    const [
        defaults,
        setDefaults,
    ] = useState({
        defaultUnit:
            "piece",

        defaultPriceMode:
            "TAX_EXCLUSIVE",

        defaultTaxPercentage:
            0,
    });

    const [
        validation,
        setValidation,
    ] = useState({
        customer: {
            valid: false,
            errors: {},
        },

        header: {
            valid: true,
            errors: {},
        },

        items: {
            valid: false,
            rowErrors: [],
            totals: {},
        },

        totals: {
            valid: true,
            errors: {},
        },
    });

    const [
        showInvoiceDetails,
        setShowInvoiceDetails,
    ] = useState(false);

    const [
        loadingAction,
        setLoadingAction,
    ] = useState("");

    const [
        pageError,
        setPageError,
    ] = useState("");

    const [
        successMessage,
        setSuccessMessage,
    ] = useState("");

    const [
        initialized,
        setInitialized,
    ] = useState(false);

    const [
        dirty,
        setDirty,
    ] = useState(false);

    const initializingRef =
        useRef(false);

    const settings =
        createData.settings ||
        createData
            .billingSettings ||
        {};

    const firms =
        getCreateDataArray(
            createData,
            "firms"
        );

    const branches =
        getCreateDataArray(
            createData,
            "branches"
        );

    const warehouses =
        getCreateDataArray(
            createData,
            "warehouses"
        );

    const salespersons =
        getCreateDataArray(
            createData,
            "salespersons",
            "salespeople",
            "users"
        );

    const paymentMethods =
        useMemo(() => {
            const configured =
                getCreateDataArray(
                    createData,
                    "paymentMethods"
                );

            if (
                configured.length
            ) {
                return configured.map(
                    (
                        method
                    ) => {
                        if (
                            typeof method ===
                            "string"
                        ) {
                            return {
                                value:
                                    method,
                                label:
                                    method.replace(
                                        /_/g,
                                        " "
                                    ),
                            };
                        }

                        return {
                            value:
                                method.value ||
                                method.code ||
                                method.key ||
                                "",

                            label:
                                method.label ||
                                method.name ||
                                method.value ||
                                method.code ||
                                "Payment",
                        };
                    }
                );
            }

            return PAYMENT_METHODS;
        }, [createData]);

    const itemTotals =
        useMemo(
            () =>
                calculateItemTotals(
                    items,
                    header
                        .isInterState
                ),
            [
                header
                    .isInterState,
                items,
            ]
        );

    const calculatedTotals =
        useMemo(
            () =>
                calculateInvoiceTotals({
                    itemTotals,

                    invoiceDiscountType:
                        totals
                            .invoiceDiscountType,

                    invoiceDiscountValue:
                        totals
                            .invoiceDiscountValue,

                    shippingCharge:
                        totals
                            .shippingCharge,

                    packingCharge:
                        totals
                            .packingCharge,

                    otherCharge:
                        totals
                            .otherCharge,

                    roundOffEnabled:
                        totals
                            .roundOffEnabled,

                    paidAmount:
                        totals
                            .paidAmount,
                }),
            [
                itemTotals,
                totals
                    .invoiceDiscountType,
                totals
                    .invoiceDiscountValue,
                totals
                    .otherCharge,
                totals
                    .packingCharge,
                totals
                    .paidAmount,
                totals
                    .roundOffEnabled,
                totals
                    .shippingCharge,
            ]
        );

    const documentStatus =
        selectedInvoice
            ?.documentStatus ||
        header.documentStatus ||
        "DRAFT";

    const pageLoading =
        loadingState
            .createDataLoading ||
        loadingState
            .detailsLoading ||
        (
            !initialized &&
            (
                !createData ||
                Object.keys(
                    createData
                ).length ===
                    0
            )
        );

    const formValid =
        useMemo(() => {
            const customerErrors =
                validateCustomer(
                    customer
                );

            const itemErrors =
                items.map(
                    (
                        item
                    ) =>
                        validateInvoiceItem(
                            item,
                            {
                                allowZeroStock:
                                    settings
                                        .allowZeroStockSale ===
                                    true,

                                allowOverSelling:
                                    settings
                                        .allowNegativeStock ===
                                    true,
                            }
                        )
                );

            const totalErrors =
                validateInvoiceTotals(
                    calculatedTotals
                );

            const hasUsableItem =
                items.some(
                    (
                        item
                    ) =>
                        normalizeText(
                            item.itemName
                        ) &&
                        toNumber(
                            item.quantity
                        ) >
                            0
                );

            return (
                Object.keys(
                    customerErrors
                ).length ===
                    0 &&
                validation
                    .header.valid !==
                    false &&
                itemErrors.every(
                    (
                        error
                    ) =>
                        Object.keys(
                            error
                        ).length ===
                        0
                ) &&
                Object.keys(
                    totalErrors
                ).length ===
                    0 &&
                hasUsableItem
            );
        }, [
            calculatedTotals,
            customer,
            items,
            settings,
            validation
                .header.valid,
        ]);

    const markDirty =
        useCallback(
            (
                setter
            ) => (
                nextValue
            ) => {
                setDirty(
                    true
                );

                setter(
                    nextValue
                );
            },
            []
        );

    useEffect(() => {
        let mounted =
            true;

        const loadData =
            async () => {
                try {
                    setPageError(
                        ""
                    );

                    const createResult =
                        await dispatch(
                            fetchSaleInvoiceCreateData()
                        ).unwrap();

                    if (
                        !mounted
                    ) {
                        return;
                    }

                    if (
                        isEditMode
                    ) {
                        await dispatch(
                            fetchSaleInvoiceById(
                                id
                            )
                        ).unwrap();
                    } else {
                        const resolvedCreateData =
                            createResult ||
                            createData ||
                            {};

                        const initial =
                            createInitialFormState(
                                resolvedCreateData
                            );

                        initializingRef.current =
                            true;

                        setCustomer(
                            initial.customer
                        );

                        setHeader(
                            initial.header
                        );

                        setItems(
                            initial.items
                        );

                        setTotals(
                            initial.totals
                        );

                        setPayment(
                            initial.payment
                        );

                        setNotes(
                            initial.notes
                        );

                        setDefaults(
                            initial.defaults
                        );

                        setDirty(
                            false
                        );

                        setInitialized(
                            true
                        );

                        window.setTimeout(
                            () => {
                                initializingRef.current =
                                    false;
                            },
                            0
                        );
                    }
                } catch (
                    error
                ) {
                    if (
                        mounted
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to load sale invoice form."
                            )
                        );

                        setInitialized(
                            true
                        );
                    }
                }
            };

        loadData();

        return () => {
            mounted =
                false;

            dispatch(
                clearSelectedSaleInvoice()
            );
        };
        // Intentionally run once per route invoice ID.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        id,
        isEditMode,
    ]);

    useEffect(() => {
        if (
            !isEditMode ||
            !selectedInvoice ||
            initialized
        ) {
            return;
        }

        const mapped =
            mapInvoiceToFormState(
                selectedInvoice,
                createData
            );

        initializingRef.current =
            true;

        setCustomer(
            mapped.customer
        );

        setHeader(
            mapped.header
        );

        setItems(
            mapped.items
        );

        setTotals(
            mapped.totals
        );

        setPayment(
            mapped.payment
        );

        setNotes(
            mapped.notes
        );

        setDefaults(
            mapped.defaults
        );

        setDirty(
            false
        );

        setInitialized(
            true
        );

        window.setTimeout(
            () => {
                initializingRef.current =
                    false;
            },
            0
        );
    }, [
        createData,
        initialized,
        isEditMode,
        selectedInvoice,
    ]);

    useEffect(() => {
        const handleBeforeUnload =
            (
                event
            ) => {
                if (
                    !dirty ||
                    loadingAction
                ) {
                    return;
                }

                event.preventDefault();

                event.returnValue =
                    "";
            };

        window.addEventListener(
            "beforeunload",
            handleBeforeUnload
        );

        return () => {
            window.removeEventListener(
                "beforeunload",
                handleBeforeUnload
            );
        };
    }, [
        dirty,
        loadingAction,
    ]);

    const validateBeforeSave =
        useCallback(() => {
            const customerErrors =
                validateCustomer(
                    customer
                );

            const itemErrors =
                items.map(
                    (
                        item
                    ) =>
                        validateInvoiceItem(
                            item,
                            {
                                allowZeroStock:
                                    settings
                                        .allowZeroStockSale ===
                                    true,

                                allowOverSelling:
                                    settings
                                        .allowNegativeStock ===
                                    true,
                            }
                        )
                );

            const totalErrors =
                validateInvoiceTotals(
                    calculatedTotals
                );

            const errors = [];

            if (
                Object.keys(
                    customerErrors
                ).length
            ) {
                errors.push(
                    Object.values(
                        customerErrors
                    )[0]
                );
            }

            const firstItemError =
                itemErrors.find(
                    (
                        itemError
                    ) =>
                        Object.keys(
                            itemError
                        ).length
                );

            if (
                firstItemError
            ) {
                errors.push(
                    Object.values(
                        firstItemError
                    )[0]
                );
            }

            if (
                Object.keys(
                    totalErrors
                ).length
            ) {
                errors.push(
                    Object.values(
                        totalErrors
                    )[0]
                );
            }

            if (
                validation
                    .header.valid ===
                false
            ) {
                errors.push(
                    Object.values(
                        validation
                            .header
                            .errors ||
                        {}
                    )[0] ||
                    "Invoice details are invalid."
                );
            }

            if (
                !items.some(
                    (
                        item
                    ) =>
                        normalizeText(
                            item.itemName
                        ) &&
                        toNumber(
                            item.quantity
                        ) >
                            0
                )
            ) {
                errors.push(
                    "Add at least one valid invoice item."
                );
            }

            if (
                errors.length
            ) {
                setPageError(
                    errors[0]
                );

                window.scrollTo({
                    top: 0,
                    behavior:
                        "smooth",
                });

                return false;
            }

            setPageError(
                ""
            );

            return true;
        }, [
            calculatedTotals,
            customer,
            items,
            settings,
            validation.header,
        ]);

    const saveDraft =
        useCallback(
            async ({
                silent =
                    false,
            } = {}) => {
                if (
                    !validateBeforeSave()
                ) {
                    throw new Error(
                        "FORM_INVALID"
                    );
                }

                const payload =
                    buildInvoicePayload({
                        customer,
                        header,
                        items,
                        totals:
                            calculatedTotals,
                        notes,
                        includePayment:
                            false,
                        payment,
                    });

                let document;

                if (
                    isEditMode
                ) {
                    const result =
                        await dispatch(
                            updateSaleInvoice({
                                documentId:
                                    id,

                                payload,
                            })
                        ).unwrap();

                    document =
                        getResultDocument(
                            result
                        );
                } else {
                    const result =
                        await dispatch(
                            createSaleInvoice(
                                payload
                            )
                        ).unwrap();

                    document =
                        getResultDocument(
                            result
                        );
                }

                if (
                    !document?._id
                ) {
                    throw new Error(
                        "Sale invoice was saved, but the response did not contain an invoice ID."
                    );
                }

                setDirty(
                    false
                );

                if (
                    !silent
                ) {
                    setSuccessMessage(
                        "Sale invoice draft saved successfully."
                    );
                }

                return document;
            },
            [
                calculatedTotals,
                customer,
                dispatch,
                header,
                id,
                isEditMode,
                items,
                notes,
                payment,
                validateBeforeSave,
            ]
        );

    const confirmSavedInvoice =
        useCallback(
            async (
                document
            ) => {
                const confirmPayload = {};

                if (
                    calculatedTotals
                        .paidAmount >
                    0
                ) {
                    confirmPayload.payments = [
                        {
                            amount:
                                roundMoney(
                                    calculatedTotals
                                        .paidAmount
                                ),

                            paymentMethod:
                                payment.method,

                            method:
                                payment.method,

                            paymentDate:
                                payment
                                    .paymentDate ||
                                header
                                    .documentDate,

                            referenceNumber:
                                normalizeText(
                                    payment
                                        .referenceNumber
                                ),

                            transactionId:
                                normalizeText(
                                    payment
                                        .transactionId
                                ),

                            notes:
                                normalizeText(
                                    payment
                                        .notes
                                ),
                        },
                    ];
                }

                const result =
                    await dispatch(
                        confirmSaleInvoice({
                            documentId:
                                document._id,

                            payload:
                                confirmPayload,
                        })
                    ).unwrap();

                return (
                    getResultDocument(
                        result
                    ) ||
                    document
                );
            },
            [
                calculatedTotals
                    .paidAmount,
                dispatch,
                header
                    .documentDate,
                payment,
            ]
        );

    const handleSaveDraft =
        useCallback(
            async () => {
                try {
                    setLoadingAction(
                        ACTION_KEYS
                            .SAVE_DRAFT
                    );

                    setPageError(
                        ""
                    );

                    setSuccessMessage(
                        ""
                    );

                    const document =
                        await saveDraft();

                    if (
                        !isEditMode
                    ) {
                        history.replace(
                            `/billing/sale-invoices/${document._id}/edit`
                        );
                    }
                } catch (
                    error
                ) {
                    if (
                        error?.message !==
                        "FORM_INVALID"
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to save sale invoice draft."
                            )
                        );
                    }
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                history,
                isEditMode,
                saveDraft,
            ]
        );

    const handleSave =
        useCallback(
            async () => {
                try {
                    setLoadingAction(
                        ACTION_KEYS.SAVE
                    );

                    setPageError(
                        ""
                    );

                    setSuccessMessage(
                        ""
                    );

                    const draft =
                        await saveDraft({
                            silent:
                                true,
                        });

                    const confirmed =
                        await confirmSavedInvoice(
                            draft
                        );

                    setDirty(
                        false
                    );

                    setSuccessMessage(
                        "Sale invoice confirmed successfully."
                    );

                    history.push(
                        `/billing/sale-invoices/${confirmed._id}/view`
                    );
                } catch (
                    error
                ) {
                    if (
                        error?.message !==
                        "FORM_INVALID"
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to confirm sale invoice."
                            )
                        );
                    }
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                confirmSavedInvoice,
                history,
                saveDraft,
            ]
        );

    const handleSaveAndPrint =
        useCallback(
            async () => {
                try {
                    setLoadingAction(
                        ACTION_KEYS
                            .SAVE_AND_PRINT
                    );

                    setPageError(
                        ""
                    );

                    setSuccessMessage(
                        ""
                    );

                    const draft =
                        await saveDraft({
                            silent:
                                true,
                        });

                    const confirmed =
                        draft
                            .documentStatus ===
                            "CONFIRMED"
                            ? draft
                            : await confirmSavedInvoice(
                                draft
                            );

                    setDirty(
                        false
                    );

                    history.push(
                        `/billing/sale-invoices/${confirmed._id}/print`
                    );
                } catch (
                    error
                ) {
                    if (
                        error?.message !==
                        "FORM_INVALID"
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to save and print sale invoice."
                            )
                        );
                    }
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                confirmSavedInvoice,
                history,
                saveDraft,
            ]
        );

    const handlePreview =
        useCallback(
            async () => {
                try {
                    setLoadingAction(
                        ACTION_KEYS
                            .PREVIEW
                    );

                    const document =
                        isEditMode
                            ? {
                                _id: id,
                            }
                            : await saveDraft({
                                silent:
                                    true,
                            });

                    setDirty(
                        false
                    );

                    history.push(
                        `/billing/sale-invoices/${document._id}/view`
                    );
                } catch (
                    error
                ) {
                    if (
                        error?.message !==
                        "FORM_INVALID"
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to preview sale invoice."
                            )
                        );
                    }
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                history,
                id,
                isEditMode,
                saveDraft,
            ]
        );

    const handleClose =
        useCallback(() => {
            if (
                dirty &&
                !window.confirm(
                    "You have unsaved changes. Leave this page?"
                )
            ) {
                return;
            }

            history.push(
                LIST_ROUTE
            );
        }, [
            dirty,
            history,
        ]);

    const handlePrint =
        useCallback(() => {
            if (
                isEditMode
            ) {
                history.push(
                    `/billing/sale-invoices/${id}/print`
                );
            }
        }, [
            history,
            id,
            isEditMode,
        ]);

    const handleDownload =
        useCallback(() => {
            if (
                isEditMode
            ) {
                history.push(
                    `/billing/sale-invoices/${id}/print?download=1`
                );
            }
        }, [
            history,
            id,
            isEditMode,
        ]);

    const handleDuplicate =
        useCallback(() => {
            history.push(
                `${CREATE_ROUTE}?duplicate=${id}`
            );
        }, [
            history,
            id,
        ]);

    if (
        pageLoading
    ) {
        return (
            <div className="sale-invoice-form-loading">
                <FiLoader />

                <strong>
                    Loading sale invoice...
                </strong>

                <span>
                    Preparing billing settings and invoice details.
                </span>
            </div>
        );
    }

    return (
        <div className="sale-invoice-form-page">
            <header className="sale-invoice-form-page-header">
                <div className="sale-invoice-form-page-title">
                    <button
                        type="button"
                        className="sale-invoice-form-back-button"
                        onClick={
                            handleClose
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <span className="sale-invoice-form-title-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <h1>
                            {isEditMode
                                ? "Edit Sale Invoice"
                                : "Create Sale Invoice"}
                        </h1>

                        <p>
                            Create a GST-ready invoice using saved or manual customers and inventory or manual items.
                        </p>
                    </div>
                </div>

                <div className="sale-invoice-form-status-area">
                    <span
                        className={`sale-invoice-form-status sale-invoice-form-status-${String(
                            documentStatus
                        ).toLowerCase()}`}
                    >
                        {documentStatus.replace(
                            /_/g,
                            " "
                        )}
                    </span>

                    {dirty && (
                        <span className="sale-invoice-form-unsaved">
                            Unsaved changes
                        </span>
                    )}
                </div>
            </header>

            {pageError && (
                <div className="sale-invoice-form-alert sale-invoice-form-alert-error">
                    <FiAlertCircle />

                    <span>
                        {pageError}
                    </span>
                </div>
            )}

            {successMessage && (
                <div className="sale-invoice-form-alert sale-invoice-form-alert-success">
                    <FiCheckCircle />

                    <span>
                        {successMessage}
                    </span>
                </div>
            )}

            <main className="sale-invoice-form-workspace">
                <CustomerSection
                    value={
                        customer
                    }
                    onChange={
                        markDirty(
                            setCustomer
                        )
                    }
                    onValidationChange={(
                        result
                    ) =>
                        setValidation(
                            (
                                previous
                            ) => ({
                                ...previous,

                                customer:
                                    result,
                            })
                        )
                    }
                    disabled={
                        Boolean(
                            loadingAction
                        )
                    }
                    required
                    allowManual
                    showShippingAddress
                />

                <section className="sale-invoice-optional-section">
                    <button
                        type="button"
                        className={
                            showInvoiceDetails
                                ? "sale-invoice-optional-toggle sale-invoice-optional-toggle-active"
                                : "sale-invoice-optional-toggle"
                        }
                        aria-expanded={
                            showInvoiceDetails
                        }
                        onClick={() =>
                            setShowInvoiceDetails(
                                (
                                    current
                                ) =>
                                    !current
                            )
                        }
                    >
                        <span className="sale-invoice-optional-toggle-icon">
                            <FiSettings />
                        </span>

                        <span className="sale-invoice-optional-toggle-content">
                            <strong>
                                Invoice Details
                            </strong>

                            <small>
                                Date, due date, payment terms, business location, supply and transport
                            </small>
                        </span>

                        {showInvoiceDetails ? (
                            <FiChevronUp />
                        ) : (
                            <FiChevronDown />
                        )}
                    </button>

                    {showInvoiceDetails && (
                        <div className="sale-invoice-optional-panel">
                            <InvoiceHeader
                                value={
                                    header
                                }
                                onChange={
                                    markDirty(
                                        setHeader
                                    )
                                }
                                onValidationChange={(
                                    result
                                ) =>
                                    setValidation(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            header:
                                                result,
                                        })
                                    )
                                }
                                documentType="SALE_INVOICE"
                                disabled={
                                    Boolean(
                                        loadingAction
                                    )
                                }
                                branches={
                                    branches
                                }
                                firms={
                                    firms
                                }
                                warehouses={
                                    warehouses
                                }
                                salespersons={
                                    salespersons
                                }
                                showDueDate
                                showReference
                                showPurchaseOrder
                                showSalesperson
                                showSalesChannel
                                showSourceType
                                showPlaceOfSupply
                                showBranch
                                showFirm
                                showWarehouse
                                showShipping
                            />
                        </div>
                    )}
                </section>

                <InvoiceItemsTable
                    items={
                        items
                    }
                    onChange={
                        markDirty(
                            setItems
                        )
                    }
                    onValidationChange={(
                        result
                    ) =>
                        setValidation(
                            (
                                previous
                            ) => ({
                                ...previous,

                                items:
                                    result,
                            })
                        )
                    }
                    defaults={
                        defaults
                    }
                    disabled={
                        Boolean(
                            loadingAction
                        )
                    }
                    allowManual
                    allowZeroStock={
                        settings
                            .allowZeroStockSale ===
                        true
                    }
                    allowOverSelling={
                        settings
                            .allowNegativeStock ===
                        true
                    }
                    isInterState={
                        header
                            .isInterState
                    }
                    minimumRows={
                        1
                    }
                    showDescription
                    showCess={
                        settings
                            .enableCess ===
                        true
                    }
                    showPriceMode
                    showStock
                />

                <InvoiceTotals
                    itemTotals={
                        itemTotals
                    }
                    value={
                        totals
                    }
                    onChange={(
                        nextTotals
                    ) => {
                        if (
                            initializingRef.current
                        ) {
                            setTotals(
                                nextTotals
                            );

                            return;
                        }

                        setDirty(
                            true
                        );

                        setTotals(
                            nextTotals
                        );
                    }}
                    onValidationChange={(
                        result
                    ) =>
                        setValidation(
                            (
                                previous
                            ) => ({
                                ...previous,

                                totals:
                                    result,
                            })
                        )
                    }
                    disabled={
                        Boolean(
                            loadingAction
                        )
                    }
                    documentType="SALE_INVOICE"
                    showPayment
                    showCharges
                    allowRoundOff
                    showTaxBreakup
                />

                {calculatedTotals
                    .paidAmount >
                    0 && (
                    <section className="sale-invoice-payment-section">
                        <div className="sale-invoice-section-heading">
                            <span>
                                <FiCreditCard />
                            </span>

                            <div>
                                <h2>
                                    Initial Payment
                                </h2>

                                <p>
                                    Provide payment details for the amount received with this invoice.
                                </p>
                            </div>
                        </div>

                        <div className="sale-invoice-payment-grid">
                            <div className="sale-invoice-form-field">
                                <label>
                                    Payment Method
                                </label>

                                <select
                                    value={
                                        payment.method
                                    }
                                    disabled={
                                        Boolean(
                                            loadingAction
                                        )
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        setDirty(
                                            true
                                        );

                                        setPayment(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                method:
                                                    event.target
                                                        .value,
                                            })
                                        );
                                    }}
                                >
                                    {paymentMethods.map(
                                        (
                                            method
                                        ) => (
                                            <option
                                                key={
                                                    method.value
                                                }
                                                value={
                                                    method.value
                                                }
                                            >
                                                {method.label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Payment Date
                                </label>

                                <input
                                    type="date"
                                    value={
                                        payment
                                            .paymentDate
                                    }
                                    disabled={
                                        Boolean(
                                            loadingAction
                                        )
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        setDirty(
                                            true
                                        );

                                        setPayment(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                paymentDate:
                                                    event.target
                                                        .value,
                                            })
                                        );
                                    }}
                                />
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Reference Number
                                </label>

                                <input
                                    type="text"
                                    value={
                                        payment
                                            .referenceNumber
                                    }
                                    disabled={
                                        Boolean(
                                            loadingAction
                                        )
                                    }
                                    placeholder="UPI, cheque or bank reference"
                                    onChange={(
                                        event
                                    ) => {
                                        setDirty(
                                            true
                                        );

                                        setPayment(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                referenceNumber:
                                                    event.target
                                                        .value,
                                            })
                                        );
                                    }}
                                />
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Transaction ID
                                </label>

                                <input
                                    type="text"
                                    value={
                                        payment
                                            .transactionId
                                    }
                                    disabled={
                                        Boolean(
                                            loadingAction
                                        )
                                    }
                                    placeholder="Optional transaction ID"
                                    onChange={(
                                        event
                                    ) => {
                                        setDirty(
                                            true
                                        );

                                        setPayment(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                transactionId:
                                                    event.target
                                                        .value,
                                            })
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </section>
                )}

                <section className={`sale-invoice-notes-section ${
                    notesOpen
                        ? "sale-invoice-notes-section-open"
                        : ""
                }`}>
                    <button
                        type="button"
                        className="sale-invoice-notes-toggle"
                        onClick={() =>
                            setNotesOpen(
                                (current) =>
                                    !current
                            )
                        }
                    >
                        <span className="sale-invoice-notes-toggle-icon">
                            <FiMessageSquare />
                        </span>

                        <span className="sale-invoice-notes-toggle-copy">
                            <strong>
                                Notes & Terms
                            </strong>

                            <small>
                                Customer notes, terms, internal remarks and footer text
                            </small>
                        </span>

                        <span className="sale-invoice-notes-toggle-status">
                            {notesOpen
                                ? "Hide"
                                : "Add / Edit"}

                            {notesOpen ? (
                                <FiChevronUp />
                            ) : (
                                <FiChevronDown />
                            )}
                        </span>
                    </button>

                    {notesOpen && (
                        <div className="sale-invoice-notes-grid">
                            <div className="sale-invoice-form-field">
                                <label>
                                    Customer Notes
                                </label>

                                <textarea
                                    rows="2"
                                    value={notes.customerNotes}
                                    disabled={Boolean(loadingAction)}
                                    placeholder="Visible to customer"
                                    onChange={(event) => {
                                        setDirty(true);
                                        setNotes((previous) => ({
                                            ...previous,
                                            customerNotes: event.target.value,
                                        }));
                                    }}
                                />
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Terms & Conditions
                                </label>

                                <textarea
                                    rows="2"
                                    value={notes.termsAndConditions}
                                    disabled={Boolean(loadingAction)}
                                    placeholder="Payment, warranty and return terms"
                                    onChange={(event) => {
                                        setDirty(true);
                                        setNotes((previous) => ({
                                            ...previous,
                                            termsAndConditions: event.target.value,
                                        }));
                                    }}
                                />
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Internal Notes
                                </label>

                                <textarea
                                    rows="2"
                                    value={notes.internalNotes}
                                    disabled={Boolean(loadingAction)}
                                    placeholder="Only visible to staff"
                                    onChange={(event) => {
                                        setDirty(true);
                                        setNotes((previous) => ({
                                            ...previous,
                                            internalNotes: event.target.value,
                                        }));
                                    }}
                                />
                            </div>

                            <div className="sale-invoice-form-field">
                                <label>
                                    Footer Text
                                </label>

                                <textarea
                                    rows="2"
                                    value={notes.footerText}
                                    disabled={Boolean(loadingAction)}
                                    placeholder="Thank-you or footer message"
                                    onChange={(event) => {
                                        setDirty(true);
                                        setNotes((previous) => ({
                                            ...previous,
                                            footerText: event.target.value,
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </section>

                {!formValid && (
                    <div className="sale-invoice-form-validation-note">
                        <FiAlertCircle />

                        Complete all required customer and item details before saving.
                    </div>
                )}

                <InvoiceActions
                    documentType="SALE_INVOICE"
                    documentStatus={
                        documentStatus
                    }
                    isEditMode={
                        isEditMode
                    }
                    loadingAction={
                        loadingAction
                    }
                    disabled={
                        Boolean(
                            loadingAction
                        )
                    }
                    permissions={{
                        canSaveDraft:
                            documentStatus ===
                            "DRAFT",

                        canSave:
                            documentStatus ===
                            "DRAFT",

                        canSaveAndPrint:
                            documentStatus ===
                            "DRAFT",

                        canPreview:
                            true,

                        canPrint:
                            isEditMode,

                        canDownload:
                            isEditMode,

                        canShare:
                            false,

                        canEmail:
                            false,

                        canDuplicate:
                            isEditMode,

                        canEdit:
                            false,

                        canCancel:
                            false,

                        canDelete:
                            false,

                        canConvert:
                            false,
                    }}
                    onSaveDraft={
                        handleSaveDraft
                    }
                    onSave={
                        handleSave
                    }
                    onSaveAndPrint={
                        handleSaveAndPrint
                    }
                    onPreview={
                        handlePreview
                    }
                    onPrint={
                        handlePrint
                    }
                    onDownload={
                        handleDownload
                    }
                    onDuplicate={
                        handleDuplicate
                    }
                    onClose={
                        handleClose
                    }
                />
            </main>
        </div>
    );
};

export {
    EMPTY_PAYMENT,
    EMPTY_NOTES,
    createInitialFormState,
    mapInvoiceToFormState,
    calculateItemTotals,
    buildInvoicePayload,
};

export default SaleInvoiceFormPage;
